import { ClientPersonOwnVerificationStatusesEnum } from '@DigitaleDoerfer/digitale-doerfer-api';
import type { FC, PropsWithChildren } from 'react';
import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { useGetPerson } from '@/features/api/hooks/participant/useGetPerson';
import { useFeature } from '@/features/feature-management/provider/useFeature';
import type { ContentRestrictionFeature } from '@/features/feature-management/types/contentRestrictionFeature';
import { FeaturesEnum } from '@/features/feature-management/types/features';
import { useBottomModal } from '@/features/popup/provider/useBottomModal';
import { EmailVerificationNeededView } from '@/features/verification/components/EmailVerificationNeededView';
import { EmailVerificationSuccessView } from '@/features/verification/components/EmailVerificationSuccess';
import type { VerificationContextValues } from '@/features/verification/providers/useVerification';
import { VerificationContext } from '@/features/verification/providers/useVerification';
import { LocalizationNameSpace } from '@/localization/types/localizationTypes';
import { StateView } from '@/shared/components/state-view/StateView';
import { hasItems } from '@/shared/utils/array';
import { isIterable } from '@/shared/utils/iterable';
import { isEmptyOrNull } from '@/shared/utils/string';

export const VerificationProvider: FC<PropsWithChildren> = ({ children }) => {
  const { t } = useTranslation(LocalizationNameSpace.VERIFICATION, {
    keyPrefix: 'sheets'
  });
  const { data: person, refetch } = useGetPerson();
  const { getFeature } = useFeature();
  const { clearAllBottomModals, showBottomModal } = useBottomModal();

  const showVerificationModal = useCallback(
    () =>
      showBottomModal({
        title: t('verifyMail.title'),
        offsetCloseButton: true,
        children: <EmailVerificationNeededView email={person?.email ?? ''} />
      }),
    [person?.email, showBottomModal, t]
  );

  const showSuccessModal = useCallback(
    () =>
      showBottomModal({
        title: t('successMail.title'),
        offsetCloseButton: true,
        children: (
          <EmailVerificationSuccessView
            onClose={() => clearAllBottomModals()}
          />
        )
      }),
    [clearAllBottomModals, showBottomModal, t]
  );

  const { featureValues, enabled } = getFeature<ContentRestrictionFeature>(
    FeaturesEnum.ContentRestriction
  );

  const statuses = useMemo(() => {
    let achievedVerificationStatuses =
      new Set<ClientPersonOwnVerificationStatusesEnum>();
    if (
      person?.verificationStatuses &&
      isIterable(person?.verificationStatuses)
    ) {
      achievedVerificationStatuses =
        new Set<ClientPersonOwnVerificationStatusesEnum>(
          person.verificationStatuses
        );
    }
    return achievedVerificationStatuses;
  }, [person]);

  const checkVerified = useCallback(
    (triggerModal: boolean) => {
      const { allowedStatusesAnyOf, allowedStatusesAllOf } = featureValues;

      const hasAchievedAnyOf =
        !hasItems(allowedStatusesAnyOf) ||
        allowedStatusesAnyOf.some((vStatus) => statuses.has(vStatus));

      const hasAchievedAllOf =
        !hasItems(allowedStatusesAllOf) ||
        allowedStatusesAllOf.every((vStatus) => statuses.has(vStatus));

      const isNotAchieved = (
        verificationStatus: ClientPersonOwnVerificationStatusesEnum
      ) =>
        !statuses.has(verificationStatus) &&
        (allowedStatusesAllOf?.includes(verificationStatus) ||
          (allowedStatusesAnyOf?.includes(verificationStatus) &&
            !hasAchievedAnyOf));

      if (
        isNotAchieved(ClientPersonOwnVerificationStatusesEnum.EmailVerified)
      ) {
        console.info(
          `Person has not ${ClientPersonOwnVerificationStatusesEnum.EmailVerified}`
        );
        if (triggerModal) showVerificationModal();
      }

      return hasAchievedAllOf && hasAchievedAnyOf;
    },
    [featureValues, statuses, showVerificationModal]
  );

  const isVerified = useMemo(() => checkVerified(false), [checkVerified]);

  const ensureVerified = useCallback(
    () => checkVerified(true),
    [checkVerified]
  );

  const isEmailVerificationEnabled =
    ((enabled ?? false) &&
      (featureValues.allowedStatusesAllOf?.includes(
        ClientPersonOwnVerificationStatusesEnum.EmailVerified
      ) ||
        featureValues.allowedStatusesAnyOf?.includes(
          ClientPersonOwnVerificationStatusesEnum.EmailVerified
        ))) ??
    false;

  const isPhoneVerificationEnabled =
    ((enabled ?? false) &&
      (featureValues.allowedStatusesAllOf?.includes(
        ClientPersonOwnVerificationStatusesEnum.PhoneNumberVerified
      ) ||
        featureValues.allowedStatusesAnyOf?.includes(
          ClientPersonOwnVerificationStatusesEnum.PhoneNumberVerified
        ))) ??
    false;

  const isEmailVerified =
    statuses?.has(ClientPersonOwnVerificationStatusesEnum.EmailVerified) ??
    false;

  const isPhoneNumberVerified =
    statuses.has(ClientPersonOwnVerificationStatusesEnum.PhoneNumberVerified) ||
    isEmptyOrNull(person?.phoneNumber);

  const contextValues: VerificationContextValues = useMemo(
    () => ({
      showSuccessModal,
      showVerificationModal,
      isVerified,
      isEmailVerificationEnabled,
      isEmailVerified,
      isPhoneVerificationEnabled,
      isPhoneNumberVerified,
      ensureVerified,
      refetch
    }),
    [
      ensureVerified,
      isVerified,
      isEmailVerificationEnabled,
      isEmailVerified,
      isPhoneVerificationEnabled,
      isPhoneNumberVerified,
      refetch,
      showSuccessModal,
      showVerificationModal
    ]
  );

  return (
    <VerificationContext.Provider value={contextValues}>
      <StateView asView>{children}</StateView>
    </VerificationContext.Provider>
  );
};
